/**
 * Storage factory + graceful fallback.
 *
 * Resolves the configured provider, initialises it, and degrades gracefully:
 * if the preferred provider can't initialise (e.g. Tableau Settings outside
 * Tableau, or IndexedDB blocked in a private window) it falls back through a
 * sensible chain so the extension always has *some* persistence.
 */

import type { StorageProviderKind } from "@/types";
import type { StorageProvider } from "./types";
import { CsvProvider } from "./CsvProvider";
import { IndexedDbProvider } from "./IndexedDbProvider";
import { LocalStorageProvider } from "./LocalStorageProvider";
import { TableauSettingsProvider } from "./TableauSettingsProvider";
import { RestApiProvider } from "./RestApiProvider";

export interface CreateStorageOptions {
  kind: StorageProviderKind;
  restApiBaseUrl?: string;
  restApiKey?: string;
  /** Base name for the database table set (rest provider only). */
  table?: string;
  /** Databricks connection details, forwarded to the storage API as headers. */
  connection?: {
    host?: string;
    token?: string;
    warehouse?: string;
    catalog?: string;
    schema?: string;
  };
  getToken?: () => string | Promise<string> | undefined;
}

/** Build the X-DBX-* header set from the connection details, skipping blanks. */
function connectionHeaders(
  conn: CreateStorageOptions["connection"],
): Record<string, string> {
  const headers: Record<string, string> = {};
  if (conn?.host) headers["X-DBX-Host"] = conn.host;
  if (conn?.token) headers["X-DBX-Token"] = conn.token;
  if (conn?.warehouse) headers["X-DBX-Warehouse"] = conn.warehouse;
  if (conn?.catalog) headers["X-DBX-Catalog"] = conn.catalog;
  if (conn?.schema) headers["X-DBX-Schema"] = conn.schema;
  return headers;
}

function instantiate(options: CreateStorageOptions): StorageProvider {
  switch (options.kind) {
    case "csv":
      return new CsvProvider();
    case "indexeddb":
      return new IndexedDbProvider();
    case "local":
      return new LocalStorageProvider();
    case "tableau-settings":
      return new TableauSettingsProvider();
    case "cloud":
      // Built-in cloud storage on the extension's own web service — no
      // credentials, no tables, nothing to configure.
      return new RestApiProvider({
        baseUrl: "",
        kind: "cloud",
        table: options.table,
        extraHeaders: { "X-SPC-Backend": "kv" },
        getToken: options.getToken ?? (() => options.restApiKey),
      });
    case "rest":
      return new RestApiProvider({
        baseUrl: options.restApiBaseUrl ?? "",
        table: options.table,
        extraHeaders: connectionHeaders(options.connection),
        getToken: options.getToken ?? (() => options.restApiKey),
      });
    default:
      return new CsvProvider();
  }
}

/** Fallback chain by preference; first that initialises wins. */
const FALLBACK_ORDER: StorageProviderKind[] = ["csv", "indexeddb", "local"];

export async function createStorage(
  options: CreateStorageOptions,
): Promise<StorageProvider> {
  // Try the requested provider first.
  const preferred = instantiate(options);
  try {
    await preferred.init();
    return preferred;
  } catch (err) {
    console.warn(
      `[storage] provider "${options.kind}" failed to init, falling back.`,
      err,
    );
  }

  for (const kind of FALLBACK_ORDER) {
    if (kind === options.kind) continue;
    const provider = instantiate({ ...options, kind });
    try {
      await provider.init();
      console.info(`[storage] using fallback provider "${kind}".`);
      return provider;
    } catch {
      // try next
    }
  }

  throw new Error("No storage provider could be initialised.");
}
